import { Injectable } from '@angular/core';
import 'rxjs/Rx';
import { Subject } from 'rxjs/Subject';
import { RoutingService } from '../../routing/services/routing.service';
import { ReportCreationService } from '../services/report-creation.service';
import { ReportService } from '../services/report.service';
import { Report } from '../models/report.model';
import { ReportSummary } from '../models/report-summary.model';
import { ReportRequest } from '../models/report-request.model';
import { Adjustments } from '../adjustments/models/adjustments.model';
import { AdjustmentUi } from '../adjustments/models/adjustment-ui.model';
import { SpecAdjustment } from '../adjustments/models/spec-adjustment.model';
import { SpecificationUi } from '../adjustments/models/specification-ui.model';
import { DatasetDate } from '../models/dataset.model';
import { Application } from '../../settings/enums/application.enum';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/modal-options.class';
import { ViewAdjustmentsModalComponent } from '../adjustments/components/view-adjustments.modal.component';
import { IAdjustmentView } from '../adjustments/models/view-adjustment.model';
import { GlobalSettingsService } from '../../settings/services/global-settings.service';
import { AdjustmentType } from '../enums/adjustment-type.enum';

@Injectable()
export class ReportManagementService {
  applicationTerminology: string;
  application: Application;

  hasCompletedInitialReport = false;
  hasAddedAdjustments = false;
  isSaving = false;

  onManufacturerListUpdated = new Subject<Array<AdjustmentUi>>();
  onSectorListUpdated = new Subject<Array<AdjustmentUi>>();
  onSpecificationUpdated = new Subject<Array<SpecificationUi>>();
  onDatasetsUpdated = new Subject<Array<DatasetDate>>();

  vehicles: Array<AdjustmentUi>;
  sectors: Array<AdjustmentUi>;
  specification: Array<SpecificationUi>;

  datasetDates: Array<DatasetDate>;

  specCategories = [
    { name: 'Cars', isOpen: false, filter: 1 },
    { name: 'LCVs', isOpen: false, filter: 2 },
    { name: 'HGVs', isOpen: false, filter: 3 },
    { name: 'Bikes', isOpen: false, filter: 4 }
  ];
  vehicleCategories = [
    { name: 'Cars', isOpen: false, filter: 1 },
    { name: 'LCVs', isOpen: false, filter: 2 },
    { name: 'HGVs', isOpen: false, filter: 3 },
    { name: 'Bikes', isOpen: false, filter: 4 }
  ];

    projectRequest: ReportRequest;

    constructor(private routingService: RoutingService, private reportService: ReportService,
        private reportCreationService: ReportCreationService,
        private settingsService: GlobalSettingsService,
        private modalService: BsModalService) {
      this.applicationTerminology = this.reportService.applicationTerminology;
      this.application = this.reportService.application;
      this.reportService.onDataSetsLoaded.subscribe(
          (response) => {
              this.datasetDates = response;
              this.onDatasetsUpdated.next(response);
          },
          (error) => console.log(error)
      );

    this.refreshAdjustmentData();
  }

  private refreshAdjustmentData() {
    if (this.application === Application.finance) {
      this.vehicles = undefined;
      this.sectors = undefined;
      this.specification = undefined;
      this.getAllManufacturers();
      this.getAllSectors();
      this.getAllSpecifications();
    }
  }

  verifyAdjustmentsAccess() {
      if (!this.hasCompletedInitialReport) {
          this.routingService.routeToCreateProject();
      }
  }

  resetCategoryStates() {
    this.specCategories.forEach(s => s.isOpen = false);
    this.vehicleCategories.forEach(s => s.isOpen = false);
  }

  createNewReport(projectRequest: ReportRequest) {
      this.projectRequest = projectRequest;
      this.hasCompletedInitialReport = true;
      this.isSaving = false;
    }

  saveManufacturerAdjustments() {
    this.projectRequest.valuationSettings.manufacturerAdjustments = this.vehicles;
  }

  saveSectorAdjustments() {
    this.projectRequest.valuationSettings.sectorAdjustments = this.sectors;
  }

  saveSpecificationAdjustments() {
    this.projectRequest.valuationSettings.specificationAdjustments = this.specification;
  }

  // moved to Promise as simple HTTP request that gets a single response;
  // this makes the code much more maintainable
  saveReport() {
    this.isSaving = true;
    this.reportCreationService.saveReport(this.projectRequest).then((reportSummary: ReportSummary) => {
      this.isSaving = false;
      this.refreshAdjustmentData();
      this.routingService.routeToReportSummary(reportSummary.projectId);
    })
    .catch((error) => {
      console.log(error)
    });
  }

  getAllManufacturers() {
    if (this.vehicles === undefined) {
      this.vehicles = new Array<AdjustmentUi>();
      this.reportService.getAllManufacturers().subscribe(response => {
        this.vehicles = response;
        this.onManufacturerListUpdated.next(this.vehicles);
      });
    } else {
      this.onManufacturerListUpdated.next(this.vehicles);
    }
  }

  getAllSectors() {
    if (this.sectors === undefined) {
      this.sectors = new Array<AdjustmentUi>();
      this.reportService.getAllSectors().subscribe(response => {
        this.sectors = response;
        this.onSectorListUpdated.next(this.sectors);
      });
    } else {
      this.onSectorListUpdated.next(this.sectors);
    };
  }

  getAllSpecifications() {
    if (this.specification === undefined) {
      this.specification = new Array<SpecificationUi>();
      this.reportService.getAllSpecifications().subscribe(response => {
        this.specification = response;
        this.onSpecificationUpdated.next(this.specification);
      });
    } else {
      this.onSpecificationUpdated.next(this.specification);
    };
  }

  viewAdjustments() {
    var data = this.settingsService.getSettings;

    if (data.adjustments.usedValueAdjustmentType===AdjustmentType.Percentage){
      data.adjustments.usedValueAdjustment = this.formatPercent(data.adjustments.usedValueAdjustment);
    }

    if (data.adjustments.futureValueAdjustmentType===AdjustmentType.Percentage){
      data.adjustments.futureValueAdjustment = this.formatPercent(data.adjustments.futureValueAdjustment);
    }

    let bsModalRef = this.modalService.show(ViewAdjustmentsModalComponent, { animated: true, keyboard: true, backdrop: true, ignoreBackdropClick: false });
          
    bsModalRef.content.sectors = this.getAdjustmentsForView(this.sectors.filter(row => row.usedValueAdjustment !==0 || row.futureValueAdjustment !==0));
    bsModalRef.content.vehicles = this.getAdjustmentsForView(this.vehicles.filter(row => row.usedValueAdjustment !==0 || row.futureValueAdjustment !==0));
    bsModalRef.content.specifications = this.getAdjustmentsForView(this.specification.filter(row => row.usedValueAdjustment !==0 || row.futureValueAdjustment !==0));

    bsModalRef.content.globalSettings = data.adjustments;
    bsModalRef.content.globalSettings.adjustedVatRate = this.calculateVatAdjustment(data.adjustments.vatRate);;

    bsModalRef.content.modelLoaded = true;
  }

  formatAdjustmentForView(id: string, name: string, adjustmentType: number, adjustmentAmount: number) {
    var results: IAdjustmentView[] = [];
    var parsedAdjustmentType = parseInt(adjustmentType.toString());
    return { id: id, name: name, amount: { total: parsedAdjustmentType===AdjustmentType.Amount ? adjustmentAmount.toString() : "", type: parsedAdjustmentType },
    percent: { total: parsedAdjustmentType===AdjustmentType.Percentage ? this.formatPercent(adjustmentAmount).toString() : "", type: parsedAdjustmentType }};
  }

  formatPercent = (val: number) => {
    return (val/100)
  }

  calculateVatAdjustment = (globalVatRate: number) => {
    return this.formatPercent(globalVatRate-20);
  }

  getAdjustmentsForView(data: Array<AdjustmentUi>){
    
    var results: IAdjustmentView[] = [];

    data.filter(adj => adj.usedValueAdjustment !==0).map(adj => results.push(this.formatAdjustmentForView(adj.id, `${adj.name} - Used`, adj.usedValueAdjustmentType, adj.usedValueAdjustment)));
    data.filter(adj => adj.futureValueAdjustment !==0).map(adj => results.push(this.formatAdjustmentForView(adj.id, `${adj.name} - Future`, adj.futureValueAdjustmentType, adj.futureValueAdjustment)));
    
    return results;
  }


}



// WEBPACK FOOTER //
// ./src/app/report/services/report-management.service.ts